// React / Next
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';

// Components
import { FullLogo, Typography } from '@archieai/ui';
import { LetterSwapPingPong } from '../ui/LetterSwap/LetterSwap';

// Hooks
import { useLenis } from 'lenis/react';
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Misc
import { motion, AnimatePresence } from 'motion/react';

// Types
type NavItem = {
  label: string;
  href: string;
};

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', href: '#hero' },
  { label: 'Who are you', href: '#who-are-you' },
  { label: 'Capabilities', href: '#capabilities' },
  { label: 'Use Cases', href: '#use-cases' },
  { label: 'Security', href: '#security' },
  { label: 'Resources', href: '/resources' },
];

// Animation variants
const menuVariants = {
  closed: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
      when: 'afterChildren' as const,
    },
  },
  open: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
      when: 'beforeChildren' as const,
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const menuItemVariants = {
  closed: {
    opacity: 0,
    y: 40,
    clipPath: 'inset(100% 0% 0% 0%)',
  },
  open: {
    opacity: 1,
    y: 0,
    clipPath: 'inset(0% 0% 0% 0%)',
    transition: {
      type: 'spring' as const,
      stiffness: 100,
      damping: 12,
      mass: 0.8,
    },
  },
};

const ctaVariants = {
  closed: {
    opacity: 0,
    y: 20,
  },
  open: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 120,
      damping: 14,
      delay: 0.35,
    },
  },
};

export const Navbar = () => {
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isOnLandingPage = location.pathname === '/';

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMenuOpen(false);

    // Check if it's a page route (starts with /) vs a hash link (starts with #)
    const isPageRoute = href.startsWith('/');

    if (isPageRoute) {
      // Navigate to the page route
      navigate({ to: href });
      return;
    }

    if (isOnLandingPage) {
      // Already on landing page, just scroll to section
      setTimeout(() => {
        if (lenis) {
          lenis.scrollTo(href, { offset: -80 });
        }
      }, 100);
    } else {
      // Navigate to landing page with hash, then scroll after navigation
      navigate({ to: '/', hash: href.replace('#', '') }).then(() => {
        // Wait for navigation and DOM to be ready, then scroll
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 150);
      });
    }
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      lenis?.stop();
    } else {
      document.body.style.overflow = '';
      lenis?.start();
    }
    return () => {
      document.body.style.overflow = '';
      lenis?.start();
    };
  }, [isMenuOpen, lenis]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <header
      className="sticky top-0 z-[100] flex justify-center bg-white border-b border-gray-100"
      role="banner"
    >
      <nav
        className="flex items-center justify-between w-full max-w-[1400px] px-[var(--spacing-96)] py-[var(--spacing-8)] max-xl:px-[var(--spacing-48)] max-lg:px-[var(--spacing-24)] max-md:px-[var(--spacing-16)] max-md:py-[var(--spacing-12)]"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center shrink-0 no-underline"
          aria-label="Archie Home"
          onClick={() => isMenuOpen && setIsMenuOpen(false)}
        >
          <FullLogo size={20} colorVariant="orange" />
        </Link>

        {/* Desktop Navigation Links */}
        <ul
          className="flex items-center gap-[var(--spacing-32)] list-none p-0 m-0 max-xl:gap-[var(--spacing-24)] max-lg:gap-[var(--spacing-16)] max-md:hidden"
          role="navigation"
        >
          {NAV_ITEMS.map((item) => (
            <li key={item.href}>
              <a
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="font-body text-tiny text-gray-600 no-underline transition-colors duration-200 whitespace-nowrap hover:text-gray-800 focus:text-gray-800 cursor-pointer"
              >
                <LetterSwapPingPong
                  label={item.label}
                  staggerFrom="first"
                  staggerDuration={0.02}
                  transition={{ type: 'spring', duration: 0.5 }}
                />
              </a>
            </li>
          ))}
        </ul>

        {/* Desktop CTA Button */}
        <Link
          to="/contact-sales"
          className="shrink-0 max-md:hidden"
          onClick={() =>
            trackEvent('cta_clicked', {
              cta_text: 'Contact Sales',
              cta_location: 'navbar_desktop',
              cta_href: '/contact-sales',
            })
          }
        >
          <button
            className="inline-flex items-center justify-center gap-[var(--spacing-8)] px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-[var(--radius-8)] font-body text-[16px] leading-[24px] font-normal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-orange-800 text-white hover:bg-orange-1000 focus:ring-orange-800"
          >
            Contact Sales
          </button>
        </Link>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="hidden max-md:flex flex-col justify-center items-center w-10 h-10 gap-[5px] cursor-pointer bg-transparent border-none"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
          <motion.span
            animate={{
              rotate: isMenuOpen ? 45 : 0,
              y: isMenuOpen ? 7 : 0,
            }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20,
              delay: isMenuOpen ? 0 : 0.1,
            }}
            className="w-6 h-[2px] bg-blue-1000 rounded-full origin-center"
          />
          <motion.span
            animate={{
              opacity: isMenuOpen ? 0 : 1,
              scaleX: isMenuOpen ? 0 : 1,
            }}
            transition={{
              duration: 0.15,
              ease: 'easeInOut',
            }}
            className="w-6 h-[2px] bg-blue-1000 rounded-full"
          />
          <motion.span
            animate={{
              rotate: isMenuOpen ? -45 : 0,
              y: isMenuOpen ? -7 : 0,
            }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20,
              delay: isMenuOpen ? 0.05 : 0.15,
            }}
            className="w-6 h-[2px] bg-blue-1000 rounded-full origin-center"
          />
        </button>

        {/* Mobile Menu Overlay - Full screen to avoid stacking context issues */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-0 bg-white z-[200] flex flex-col"
            >
              {/* Navbar area inside overlay - Logo and Close button */}
              <div className="flex items-center justify-between w-full px-[var(--spacing-16)] py-[var(--spacing-12)] border-b border-gray-100">
                <Link
                  to="/"
                  className="flex items-center shrink-0 no-underline"
                  aria-label="Archie Home"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FullLogo size={20} colorVariant="orange" />
                </Link>

                {/* Close button */}
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="flex flex-col justify-center items-center w-10 h-10 gap-[5px] cursor-pointer bg-transparent border-none"
                  aria-label="Close menu"
                >
                  <motion.span
                    initial={{ rotate: 0, y: 0 }}
                    animate={{ rotate: 45, y: 7 }}
                    className="w-6 h-[2px] bg-blue-1000 rounded-full origin-center"
                  />
                  <motion.span
                    initial={{ opacity: 1, scaleX: 1 }}
                    animate={{ opacity: 0, scaleX: 0 }}
                    className="w-6 h-[2px] bg-blue-1000 rounded-full"
                  />
                  <motion.span
                    initial={{ rotate: 0, y: 0 }}
                    animate={{ rotate: -45, y: -7 }}
                    className="w-6 h-[2px] bg-blue-1000 rounded-full origin-center"
                  />
                </button>
              </div>

              {/* Mobile Navigation Links */}
              <div className="flex-1 flex flex-col justify-between px-[var(--spacing-24)] py-[var(--spacing-48)]">
                <ul className="flex flex-col items-start gap-[var(--spacing-24)] list-none p-0 m-0">
                  {NAV_ITEMS.map((item, index) => (
                    <motion.li
                      key={item.href}
                      variants={menuItemVariants}
                      custom={index}
                      className="w-full"
                    >
                      <a
                        href={item.href}
                        onClick={(e) => handleNavClick(e, item.href)}
                        className="no-underline cursor-pointer block"
                      >
                        <motion.span
                          className="inline-block"
                          whileHover={{ x: 8 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                        >
                          <Typography variant="h3" className="text-blue-1000">
                            {item.label}
                          </Typography>
                        </motion.span>
                      </a>
                    </motion.li>
                  ))}
                </ul>

                {/* Mobile CTA Button */}
                <motion.div variants={ctaVariants}>
                  <Link
                    to="/contact-sales"
                    onClick={() => {
                      setIsMenuOpen(false);
                      trackEvent('cta_clicked', {
                        cta_text: 'Contact Sales',
                        cta_location: 'navbar_mobile',
                        cta_href: '/contact-sales',
                      });
                    }}
                  >
                    <button className="inline-flex items-center justify-center gap-[var(--spacing-8)] px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-[var(--radius-8)] font-body text-[16px] leading-[24px] font-normal transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-orange-800 text-white hover:bg-orange-1000 focus:ring-orange-800">
                      Contact Sales
                    </button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
